import api from 'api'
import { CACHE_TAG_ACCOUNT_LIST, CACHE_TAG_LATE_FEES, CACHE_TAG_LATE_FEE_VERSION_LIST } from 'api/cacheTagTypes'
import { transformSearchResponse } from 'utils/accountSearch'
import { withIdTag } from 'utils/cache'

const DEFAULT_CACHE_TIME = 3600

const extendedApi = api.injectEndpoints({
  endpoints: build => ({
    createLateFee: build.mutation({
      query: body => ({
        url: '/api/v1/core/fee/late',
        method: 'POST',
        body,
      }),
      invalidatesTags: [CACHE_TAG_LATE_FEES],
    }),
    archiveLateFee: build.mutation({
      query: ({ id }) => ({
        url: `/api/v1/core/fee/late/${id}/archive`,
        method: 'PUT',
      }),
      invalidatesTags: [CACHE_TAG_LATE_FEES],
    }),
    restoreLateFee: build.mutation({
      query: ({ id }) => ({
        url: `/api/v1/core/fee/late/${id}/restore`,
        method: 'PUT',
      }),
      invalidatesTags: [CACHE_TAG_LATE_FEES],
    }),
    fetchLateFees: build.query({
      query: body => ({
        url: `api/v1/core/fee/late`,
        method: 'GET',
        body,
      }),
      providesTags: [CACHE_TAG_LATE_FEES],
    }),
    fetchLateFeeDetails: build.query({
      query: ({ id }) => ({
        url: `/api/v1/core/fee/late/${id}`,
      }),
      providesTags: [CACHE_TAG_LATE_FEES],
    }),
    updateLateFee: build.mutation({
      query: ({ id, ...rest }) => ({
        url: `/api/v1/core/fee/late/${id}`,
        method: 'PUT',
        body: {
          ...rest,
        },
      }),
      invalidatesTags: [CACHE_TAG_LATE_FEES],
    }),
    linkAccountsToLateFee: build.mutation({
      query: ({ id, ...rest }) => ({
        url: `/api/v1/core/fee/late/${id}/link`,
        method: 'POST',
        body: {
          ...rest,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        CACHE_TAG_LATE_FEES,
        CACHE_TAG_ACCOUNT_LIST,
        ...Array.from(arg?.accounts || []).flatMap(accountId => withIdTag(CACHE_TAG_LATE_FEE_VERSION_LIST, accountId)),
      ],
    }),
    linkLateFeesToAccount: build.mutation({
      query: ({ accountId, ...rest }) => ({
        url: `/api/v1/core/accounts/${accountId}/fee/late/link`,
        method: 'POST',
        body: {
          ...rest,
        },
      }),
      invalidatesTags: (result, error, arg) => [CACHE_TAG_LATE_FEES, ...withIdTag(CACHE_TAG_LATE_FEE_VERSION_LIST, arg?.accountId)],
    }),
    unlinkLateFeesToAccount: build.mutation({
      query: ({ accountId, ...rest }) => ({
        url: `/api/v1/core/accounts/${accountId}/fee/late/unlink`,
        method: 'POST',
        body: {
          ...rest,
        },
      }),
      invalidatesTags: (result, error, arg) => [CACHE_TAG_LATE_FEES, ...withIdTag(CACHE_TAG_LATE_FEE_VERSION_LIST, arg?.accountId)],
    }),
    unlinkAccountsToLateFee: build.mutation({
      query: ({ id, ...rest }) => ({
        url: `/api/v1/core/fee/late/${id}/unlink`,
        method: 'POST',
        body: {
          ...rest,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        CACHE_TAG_LATE_FEES,
        CACHE_TAG_ACCOUNT_LIST,
        ...Array.from(arg?.accounts || []).flatMap(accountId => withIdTag(CACHE_TAG_LATE_FEE_VERSION_LIST, accountId)),
      ],
    }),
    fetchAccountsLinkedToLateFee: build.query({
      query: ({ id, includeTags = false, includeContacts = true, includeDeactivated = true }) => ({
        url: `/api/v1/core/fee/late/${id}/accounts`,
        params: {
          includeTags,
          includeContacts,
          includeDeactivated,
        },
      }),
      transformResponse: ({ accounts }) => transformSearchResponse({ results: accounts }),
      providesTags: [CACHE_TAG_ACCOUNT_LIST],
      keepUnusedDataFor: DEFAULT_CACHE_TIME,
    }),
    fetchAccountLateFees: build.query({
      query: ({ accountId }) => ({
        url: `/api/v1/core/accounts/${accountId}/fee/late`,
      }),
      providesTags: [CACHE_TAG_LATE_FEES],
      keepUnusedDataFor: DEFAULT_CACHE_TIME,
    }),
    fetchInvoicesLinkedToLateFeeAndAccount: build.query({
      query: ({ accountId, lateFeeId }) => ({
        url: `/api/v1/core/accounts/${accountId}/fee/late/${lateFeeId}`,
      }),
      providesTags: [CACHE_TAG_LATE_FEES],
    }),
    fetchLateFeeInvoicesByLateFeeIdAndAccount: build.query({
      query: ({ id, accountId }) => ({
        url: `/api/v1/core/accounts/${accountId}/fee/late/${id}/target-invoices`,
      }),
    }),
    fetchAppliedLateFeesToInvoice: build.query({
      query: ({ id }) => ({
        url: `/api/v1/billing/invoice/${id}/fee/late/target`,
      }),
    }),
  }),
})

export const {
  useCreateLateFeeMutation,
  useFetchLateFeesQuery,
  useLazyFetchLateFeesQuery,
  useArchiveLateFeeMutation,
  useRestoreLateFeeMutation,
  useLazyFetchLateFeeDetailsQuery,
  useUpdateLateFeeMutation,
  useLinkAccountsToLateFeeMutation,
  useLinkLateFeesToAccountMutation,
  useUnlinkLateFeesToAccountMutation,
  useUnlinkAccountsToLateFeeMutation,
  useLazyFetchAccountsLinkedToLateFeeQuery,
  useLazyFetchAccountLateFeesQuery,
  useLazyFetchLateFeeInvoicesByLateFeeIdAndAccountQuery,
  useLazyFetchInvoicesLinkedToLateFeeAndAccountQuery,
  useLazyFetchAppliedLateFeesToInvoiceQuery,
} = extendedApi
